import { useState } from 'react'
import Modal from '@cloudscape-design/components/modal'
import Table from '@cloudscape-design/components/table'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Checkbox from '@cloudscape-design/components/checkbox'
import type { WebsiteSuggestions } from '@/lib/types'

// Fields that can be auto-filled from website scraping (subset of WizardFormData)
export interface ExtractableFields {
  brandName?: string
  brandLocation?: string
  brandSpecialization?: string
  industry?: string
  phone?: string
  businessDescription?: string
  servicesOffered?: string[]
  productCategories?: string[]
  serviceArea?: string
  facebookUrl?: string
  linkedinUrl?: string
  twitterUrl?: string
  ctaUrl?: string
}

interface SuggestionRow {
  key: keyof ExtractableFields
  label: string
  current: string
  suggested: string
  isArray: boolean
}

interface Props {
  suggestions: WebsiteSuggestions
  currentValues: ExtractableFields
  websiteUrl: string
  onApply: (selected: ExtractableFields) => void
  onDismiss: () => void
}

const FIELD_LABELS: Record<keyof ExtractableFields, string> = {
  brandName: 'Brand name',
  brandLocation: 'Brand location',
  brandSpecialization: 'Brand specialization',
  industry: 'Industry',
  phone: 'Phone',
  businessDescription: 'Business description',
  servicesOffered: 'Services offered',
  productCategories: 'Product categories',
  serviceArea: 'Service area',
  facebookUrl: 'Facebook URL',
  linkedinUrl: 'LinkedIn URL',
  twitterUrl: 'Twitter/X URL',
  ctaUrl: 'CTA URL',
}

function formatValue(value: string | string[] | undefined | null): string {
  if (value == null || value === '') return ''
  if (Array.isArray(value)) return value.join(', ')
  return value
}

function buildRows(
  suggestions: WebsiteSuggestions,
  currentValues: ExtractableFields,
): SuggestionRow[] {
  const rows: SuggestionRow[] = []
  const keys = Object.keys(FIELD_LABELS) as Array<keyof ExtractableFields>
  for (const key of keys) {
    const raw = suggestions[key as keyof WebsiteSuggestions]
    if (raw == null) continue
    const suggested = formatValue(raw as string | string[])
    if (!suggested) continue
    rows.push({
      key,
      label: FIELD_LABELS[key],
      current: formatValue(currentValues[key] as string | string[] | undefined),
      suggested,
      isArray: Array.isArray(raw),
    })
  }
  return rows
}

export default function FillFromWebsiteModal({
  suggestions,
  currentValues,
  websiteUrl,
  onApply,
  onDismiss,
}: Props) {
  const rows = buildRows(suggestions, currentValues)

  const initialChecked: Record<string, boolean> = {}
  for (const row of rows) {
    initialChecked[row.key] = !row.current
  }

  const [checked, setChecked] = useState<Record<string, boolean>>(initialChecked)

  const handleApply = () => {
    const selected: ExtractableFields = {}
    for (const row of rows) {
      if (!checked[row.key]) continue
      const raw = suggestions[row.key as keyof WebsiteSuggestions]
      if (raw == null) continue
      if (row.isArray) {
        ;(selected as Record<string, unknown>)[row.key] = raw
      } else {
        ;(selected as Record<string, unknown>)[row.key] = raw
      }
    }
    onApply(selected)
    onDismiss()
  }

  const selectedCount = Object.values(checked).filter(Boolean).length

  const domain = (() => {
    try { return new URL(websiteUrl).hostname } catch { return websiteUrl }
  })()

  return (
    <Modal
      visible
      size="large"
      header={`Fill from website — ${domain}`}
      onDismiss={onDismiss}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={selectedCount === 0}
              onClick={handleApply}
            >
              Apply {selectedCount > 0 ? `${selectedCount} field${selectedCount > 1 ? 's' : ''}` : 'selected'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          Review the fields extracted from the website. Checked fields will be applied to the form.
          Fields that are already filled are unchecked by default.
        </Box>
        <Table
          variant="embedded"
          trackBy="key"
          items={rows}
          columnDefinitions={[
            {
              id: 'field',
              header: 'Field',
              cell: (row) => row.label,
              width: 180,
            },
            {
              id: 'current',
              header: 'Current value',
              cell: (row) =>
                row.current ? (
                  <Box color="text-body-secondary">{row.current}</Box>
                ) : (
                  <Box color="text-status-inactive">—</Box>
                ),
            },
            {
              id: 'suggested',
              header: 'Suggested value',
              cell: (row) => row.suggested,
            },
            {
              id: 'apply',
              header: 'Apply',
              cell: (row) => (
                <Checkbox
                  checked={!!checked[row.key]}
                  onChange={({ detail }) =>
                    setChecked((prev) => ({ ...prev, [row.key]: detail.checked }))
                  }
                />
              ),
              width: 80,
            },
          ]}
        />
      </SpaceBetween>
    </Modal>
  )
}
